import { keepPreviousData, useQuery, type QueryClient } from '@tanstack/react-query';
import {
  fetchCommissionStatistics,
  fetchDashboardBootstrap,
  fetchDashboardZones,
  fetchOrderStatistics,
  fetchUserStatistics,
  type DashboardApiFilters,
} from '@/api/dashboard';

function filterKey(filters: DashboardApiFilters): string {
  return JSON.stringify({
    zone_id: filters.zone_id ?? 'all',
    statistics_type: filters.statistics_type ?? 'overall',
    date_from: filters.date_from ?? null,
    date_to: filters.date_to ?? null,
    commission_overview: filters.commission_overview ?? null,
    user_overview: filters.user_overview ?? null,
  });
}

/**
 * Prefetch the combined dashboard bootstrap and seed the individual query keys
 * used by DashboardPage widgets so first paint is instant after login.
 */
export async function prefetchDashboard(
  qc: QueryClient,
  filters: DashboardApiFilters = {},
): Promise<void> {
  const key = filterKey(filters);
  const salesPeriod = filters.commission_overview ?? 'this_year';
  const userPeriod = filters.user_overview ?? 'overall';
  const zoneKey = String(filters.zone_id ?? 'all');

  try {
    const res = await qc.fetchQuery({
      queryKey: ['dashboard', 'bootstrap', key],
      queryFn: () => fetchDashboardBootstrap(filters),
      staleTime: 30_000,
    });
    const d = res.data;

    qc.setQueryData(['dashboard', 'order-stats', key], {
      data: { stats: d.stats, params: d.params, moduleType: d.moduleType },
    });
    qc.setQueryData(['dashboard', 'sales', key, salesPeriod], {
      data: {
        grossSale: d.grossSale,
        commission: d.commission,
        deliveryCommission: d.deliveryCommission,
        label: d.label,
        params: d.params,
      },
    });
    qc.setQueryData(['dashboard', 'users', key, userPeriod], {
      data: { userOverview: d.userOverview, params: d.params },
    });
    // UserDistribution calls useDashboardUsers({ zone_id, user_overview }) — narrower key.
    const userDistKey = filterKey({
      zone_id: filters.zone_id,
      user_overview: filters.user_overview,
    });
    qc.setQueryData(['dashboard', 'users', userDistKey, userPeriod], {
      data: { userOverview: d.userOverview, params: d.params },
    });
    qc.setQueryData(['dashboard', 'rankings', zoneKey], {
      data: {
        stats: d.stats,
        topSellingItems: d.topSellingItems,
        topRatedItems: d.topRatedItems,
        topRestaurants: d.topRestaurants,
        popularRestaurants: d.popularRestaurants,
        topDeliverymen: d.topDeliverymen,
        topCustomers: d.topCustomers,
        grossSale: d.grossSale,
        commission: d.commission,
        deliveryCommission: d.deliveryCommission,
        params: d.params,
      },
    });
  } catch {
    // Non-fatal: individual widget queries will fetch on mount.
  }

  // Zones dropdown is independent and long-lived.
  void qc.prefetchQuery({
    queryKey: ['dashboard', 'zones'],
    queryFn: fetchDashboardZones,
    staleTime: 5 * 60_000,
  });
}

export function useDashboardOrderStatistics(filters: DashboardApiFilters) {
  return useQuery({
    queryKey: ['dashboard', 'order-stats', filterKey(filters)],
    queryFn: () => fetchOrderStatistics(filters),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useDashboardSales(filters: DashboardApiFilters) {
  return useQuery({
    queryKey: ['dashboard', 'sales', filterKey(filters), filters.commission_overview ?? 'this_year'],
    queryFn: () => fetchCommissionStatistics(filters),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useDashboardUsers(filters: DashboardApiFilters) {
  return useQuery({
    queryKey: ['dashboard', 'users', filterKey(filters), filters.user_overview ?? 'overall'],
    queryFn: () => fetchUserStatistics(filters),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useDashboardZones() {
  return useQuery({
    queryKey: ['dashboard', 'zones'],
    queryFn: fetchDashboardZones,
    staleTime: 5 * 60_000,
    select: (data) => data.zones ?? [],
  });
}
